/**
 * The single host environment in which graphics templates run, shared by the
 * browser preview (sandboxed iframe) and the export renderer (headless
 * Chromium). Using the same host is what makes preview and export agree.
 *
 * The host:
 *  - forbids all network access (CSP: nothing but inline code and data: URIs);
 *  - freezes Date, performance.now and Math.random, and disables CSS
 *    animations/transitions and requestAnimationFrame, so output depends only
 *    on the requested media time;
 *  - exposes window.__prismRender(t, data, info) that awaits fonts then calls
 *    the template's window.prism.renderAt.
 */
import { CAPTION, DEFAULT_TRACK_STYLE, cueBoxCss, cueContainerCss, cueLayout, cueRuns, stripLegacyAlign, type CueRun, type ResolvedCueStyle } from './caption.ts';

const CSP = [
  "default-src 'none'",
  "script-src 'unsafe-inline'",
  "style-src 'unsafe-inline'",
  'font-src data:',
  'img-src data:',
].join('; ');

const SHIM = `
(() => {
  const fixed = 1767225600000; // 2026-01-01T00:00:00Z
  const RealDate = Date;
  class FrozenDate extends RealDate { constructor(...a) { super(...(a.length ? a : [fixed])); } static now() { return fixed; } }
  window.Date = FrozenDate;
  let seed = 0x2f6b1d3;
  Math.random = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
  if (window.performance) performance.now = () => 0;
  window.requestAnimationFrame = () => 0;
  window.setInterval = () => 0;
  window.fetch = () => Promise.reject(new Error('Network access is disabled for graphics templates'));
  window.XMLHttpRequest = undefined;
  window.WebSocket = undefined;
  window.__prismRender = async (t, data, info) => {
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
    if (!window.prism || typeof window.prism.renderAt !== 'function') throw new Error('Template does not define window.prism.renderAt');
    window.prism.renderAt(t, data || {}, info);
    return true;
  };
  // Tell an embedding preview that the host is listening (no-op when rendered headlessly).
  window.addEventListener('load', () => { try { if (parent !== window) parent.postMessage({ type: 'prism:ready' }, '*'); } catch (e) {} });
  window.addEventListener('message', async (e) => {
    const m = e.data;
    if (!m || m.type !== 'prism:render') return;
    try {
      await window.__prismRender(m.t, m.data, m.info);
      parent.postMessage({ type: 'prism:rendered', id: m.id }, '*');
    } catch (err) {
      parent.postMessage({ type: 'prism:error', id: m.id, message: String(err && err.message || err) }, '*');
    }
  });
})();
`;

const NO_MOTION = '*,*::before,*::after{animation:none!important;transition:none!important}';

/** Wrap a self-contained template bundle in the shared host. */
export function buildGraphicsHost(bundleHtml: string): string {
  // Transparent canvas: the declared colour scheme matches the (dark) embedding page, which keeps a
  // cross-origin preview frame transparent; headless export captures with an omitted background.
  const head = `<meta http-equiv="Content-Security-Policy" content="${CSP}"><meta name="color-scheme" content="dark"><script>${SHIM}</script><style>${NO_MOTION}html,body{background:transparent!important}</style>`;
  if (/<head[^>]*>/i.test(bundleHtml)) return bundleHtml.replace(/<head([^>]*)>/i, `<head$1>${head}`);
  return `<!doctype html><html><head>${head}</head><body>${bundleHtml}</body></html>`;
}

/* ---------------------------------------------------------------- OGraf host (0.93.0) */

/**
 * The OGraf host's policy: no network; inline code; and the blob: URLs the host makes itself from the package bytes
 * it is given (the graphic's module, fonts, images and media).
 */
export const OGRAF_HOST_CSP = [
  "default-src 'none'",
  "script-src 'unsafe-inline' blob:",
  "style-src 'unsafe-inline' blob:",
  'font-src data: blob:',
  'img-src data: blob:',
  'media-src data: blob:',
].join('; ');

const OGRAF_SHIM = `
(() => {
  const fixed = 1767225600000; // 2026-01-01T00:00:00Z
  const RealDate = Date;
  class FrozenDate extends RealDate { constructor(...a) { super(...(a.length ? a : [fixed])); } static now() { return fixed; } }
  window.Date = FrozenDate;
  let seed = 0x2f6b1d3;
  Math.random = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
  if (window.performance) performance.now = () => 0;
  // Kept for the host itself (painted, below), before graphics lose it.
  const hostRaf = window.requestAnimationFrame.bind(window);
  window.requestAnimationFrame = () => 0;
  window.setInterval = () => 0;
  window.fetch = () => Promise.reject(new Error('Network access is disabled for graphics'));
  window.XMLHttpRequest = undefined;
  window.WebSocket = undefined;

  const stage = () => document.getElementById('stage');
  const packages = new Map();   // key -> { urls: { path: blob URL }, bytes: { path: { data, mime } } }
  const instances = new Map();  // id -> { el, z }
  let counter = 0;
  // An OGraf method answers { statusCode, statusMessage }; anything but 2xx is a failure (0.94.0: never a frame).
  const ok = (r, what) => {
    const code = r && typeof r === 'object' && typeof r.statusCode === 'number' ? r.statusCode : 200;
    if (code < 200 || code > 299) {
      const err = new Error(what + ' returned ' + code + (r && r.statusMessage ? ': ' + r.statusMessage : ''));
      err.prismStatus = code;
      throw err;
    }
    return r;
  };

  function setStage(width, height) {
    const s = stage();
    s.style.width = width + 'px';
    s.style.height = height + 'px';
    return true;
  }

  /** The files of one package version, as bytes; the host makes its own blob: URLs for them. */
  function addPackage(key, files) {
    if (packages.has(key)) return true;
    const urls = {};
    const bytes = {};
    for (const path of Object.keys(files)) {
      const f = files[path];
      bytes[path] = f;
      urls[path] = URL.createObjectURL(new Blob([f.data], { type: f.mime || 'application/octet-stream' }));
    }
    packages.set(key, { urls, bytes });
    return true;
  }

  function dropPackage(key) {
    const p = packages.get(key);
    if (!p) return true;
    for (const u of Object.values(p.urls)) URL.revokeObjectURL(u);
    packages.delete(key);
    return true;
  }

  /**
   * Load one graphic instance: import its module from a fresh blob: URL (a new module, so its class can be
   * registered under a tag of its own), define the element, load it non-real time and give it its schedule.
   * Replacing an instance keeps the old element on screen until the new one has drawn its first frame.
   */
  async function mount(id, o) {
    const p = packages.get(o.pkg);
    if (!p) throw new Error('Unknown package ' + o.pkg);
    const main = p.bytes[o.main];
    if (!main) throw new Error('The package has no ' + o.main);
    const moduleUrl = URL.createObjectURL(new Blob([main.data], { type: 'text/javascript' }));
    let mod;
    try { mod = await import(moduleUrl); } finally { URL.revokeObjectURL(moduleUrl); }
    if (!mod || typeof mod.default !== 'function') throw new Error(o.main + ' has no default export');
    const tag = 'ograf-graphic-' + (++counter);
    customElements.define(tag, mod.default);
    const el = document.createElement(tag);
    const scale = o.scale || 1;
    el.style.cssText = 'position:absolute;left:' + (o.left || 0) + 'px;top:' + (o.top || 0) + 'px;display:block;width:' + o.width + 'px;height:' + o.height + 'px;transform-origin:0 0;'
      + (scale !== 1 ? 'transform:scale(' + scale + ');' : '') + 'z-index:' + (o.z || 0) + ';visibility:hidden';
    stage().append(el);
    try {
      if (typeof el.load !== 'function') throw new Error('The graphic does not implement load()');
      ok(await el.load({
        data: o.data || {}, renderType: 'non-realtime',
        renderCharacteristics: { resolution: { width: o.width, height: o.height }, frameRate: o.frameRate.num / o.frameRate.den, accessToPublicInternet: false, v_frameRate: { num: o.frameRate.num, den: o.frameRate.den } },
        v_assetUrls: p.urls,
      }), 'load');
      ok(await el.setActionsSchedule({ schedule: o.schedule || [] }), 'setActionsSchedule');
      ok(await el.goToTime({ timestamp: o.at || 0 }), 'goToTime(' + (o.at || 0) + ' ms)');
    } catch (err) {
      el.remove();
      try { await el.dispose({}); } catch (e) {}
      throw err;
    }
    el.style.visibility = o.visible === false ? 'hidden' : 'visible';
    const old = instances.get(id);
    instances.set(id, { el, z: o.z || 0 });
    if (old) { old.el.remove(); try { await old.el.dispose({}); } catch (e) {} }
    return true;
  }

  /**
   * Show one instance at a moment (ms from its clip start). Resolves once goToTime has painted the frame; goToTime
   * may be async (a graphic with video waits, up to 5 s, until each video's frame is decoded) and is always awaited.
   * When it throws or answers anything but 2xx the instance is hidden (no frame of it is ever shown) and the call
   * rejects with the graphic's statusMessage.
   */
  async function seek(id, ms, visible) {
    const i = instances.get(id);
    if (!i) return false;
    try {
      ok(await i.el.goToTime({ timestamp: ms }), 'goToTime(' + ms + ' ms)');
    } catch (err) {
      i.el.style.visibility = 'hidden';
      throw err;
    }
    i.el.style.visibility = visible === false ? 'hidden' : 'visible';
    return true;
  }

  async function unmount(id) {
    const i = instances.get(id);
    if (!i) return true;
    instances.delete(id);
    i.el.remove();
    try { await i.el.dispose({}); } catch (e) {}
    return true;
  }

  /**
   * Resolves once the page has painted what the seek changed (1.0.0-rc.12): two animation frames, the first after the
   * style change and the second after its paint. goToTime only sets the state, so a screenshot taken at once could
   * still show some stale pixels (a blur's halo). A page that gets no frames (a hidden preview) goes on after 250 ms.
   */
  const painted = () => new Promise((resolve) => {
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(true); } };
    setTimeout(finish, 250);
    hostRaf(() => hostRaf(finish));
  });

  window.__prismOgraf = { setStage, addPackage, dropPackage, mount, seek, unmount, ids: () => [...instances.keys()] };
  /** Seek every instance (or one) to ms and wait until each has painted it, before answering. */
  window.__prismOgrafSeek = async (ms, id) => {
    const ids = id === undefined ? [...instances.keys()] : [id];
    await Promise.all(ids.map((x) => seek(x, ms)));
    await painted();
    return true;
  };

  window.addEventListener('load', () => { try { if (parent !== window) parent.postMessage({ type: 'prism:ready', engine: 'ograf-v1' }, '*'); } catch (e) {} });
  // The preview talks to the host by message: packages, mounts, unmounts and render requests (each answered once done).
  window.addEventListener('message', async (e) => {
    const m = e.data;
    if (!m || typeof m.type !== 'string' || (m.type !== 'prism:render' && m.type.indexOf('prism:ograf-') !== 0)) return;
    try {
      if (m.type === 'prism:ograf-stage') setStage(m.width, m.height);
      else if (m.type === 'prism:ograf-package') addPackage(m.key, m.files);
      else if (m.type === 'prism:ograf-drop') dropPackage(m.key);
      else if (m.type === 'prism:ograf-mount') await mount(m.instance, m.options);
      else if (m.type === 'prism:ograf-unmount') await unmount(m.instance);
      else if (m.type === 'prism:render') {
        // Every instance is seeked; one that fails is hidden and reported by instance, the others still paint.
        const seeks = m.seeks || [];
        const results = await Promise.allSettled(seeks.map((s) => seek(s.instance, s.ms, s.visible)));
        const errors = results.flatMap((r, k) => (r.status === 'rejected' ? [{ instance: seeks[k].instance, message: String(r.reason && r.reason.message || r.reason) }] : []));
        await painted();
        parent.postMessage({ type: 'prism:rendered', id: m.id, errors }, '*');
        return;
      }
      parent.postMessage({ type: 'prism:ograf-done', id: m.id, instance: m.instance }, '*');
    } catch (err) {
      parent.postMessage({ type: 'prism:error', id: m.id, instance: m.instance, message: String(err && err.message || err) }, '*');
    }
  });
})();
`;

/**
 * The host page for OGraf graphics, shared by the browser preview (sandboxed frame) and the renderer: no network
 * (its own blob: URLs only), the same frozen clock and transparent background as the renderAt host, and no
 * animation:none style (OGraf graphics may use CSS animations; goToTime drives them). It can hold several graphic
 * instances, stacked by their z (track order). Package files arrive as bytes ({ path → { data: ArrayBuffer, mime } }).
 *
 *   window.__prismOgraf.setStage(width, height)
 *   window.__prismOgraf.addPackage(key, files)
 *   window.__prismOgraf.mount(instanceId, { pkg, main, data, width, height, scale, z, frameRate: { num, den }, schedule, at, visible })
 *   window.__prismOgraf.seek(instanceId, ms, visible) · unmount(instanceId)
 *   window.__prismOgrafSeek(ms, instanceId?)     awaits goToTime and then a painted frame (two animation frames, 1.0.0-rc.12)
 *                                                before resolving; rejects when goToTime is not 2xx
 *
 * In the preview, a render request answers { type: 'prism:rendered', id, errors: [{ instance, message }] }: an
 * instance whose goToTime failed is hidden and listed, so the viewer shows an error on that graphic, not a frame.
 */
export function buildOgrafHost(size: { width: number; height: number } = { width: 1920, height: 1080 }): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${OGRAF_HOST_CSP}"><meta name="color-scheme" content="dark">`
    + `<script>${OGRAF_SHIM}</script><style>html,body{margin:0;padding:0;overflow:hidden;background:transparent!important}#stage{position:relative;overflow:hidden;width:${size.width}px;height:${size.height}px}</style>`
    + `</head><body><div id="stage"></div></body></html>`;
}

/** One cue of a burn-in picture, laid out in Node (subtitleFrame) so the page applies JSON only. */
export interface SubtitleFrameCue {
  /** The full-frame container's CSS (caption.ts cueContainerCss). */
  container: Record<string, string>;
  /** The text box's CSS (cueBoxCss). */
  box: Record<string, string>;
  /** The text as runs (cueRuns): plain text with italic, bold and underline flags; never HTML. */
  runs: CueRun[];
  speaker?: string;
}

/** One burn-in picture: a w × h frame with every cue shown in it. */
export interface SubtitleFrame {
  width: number;
  height: number;
  cues: SubtitleFrameCue[];
  /** The speaker label's colour and a bold run's weight (CAPTION). */
  speakerColor: string;
  boldWeight: number;
}

/**
 * The burn-in picture for one renderCues entry (1.9.0): each shown cue (`active`) in its cell and style, laid out with
 * cueLayout, its text as cueRuns; an entry without `active` (a 1.8.3 workflow's job) draws `text` and `speaker` in the
 * default style, bottom-centred, as before. `subtitleBottom` is the format's subtitle height for bottom-row cues.
 */
export function subtitleFrame(
  entry: { text: string; speaker?: string; active?: ReadonlyArray<{ text: string; speaker?: string; style: ResolvedCueStyle }> },
  output: { width: number; height: number },
  subtitleBottom?: number,
): SubtitleFrame {
  const shown = entry.active?.length ? entry.active : [{ text: stripLegacyAlign(entry.text), ...(entry.speaker ? { speaker: entry.speaker } : {}), style: { ...DEFAULT_TRACK_STYLE } }];
  return {
    width: output.width,
    height: output.height,
    speakerColor: CAPTION.speakerColor,
    boldWeight: 800,
    cues: shown.map((c) => {
      const l = cueLayout(output.width, output.height, c.style, subtitleBottom);
      return { container: cueContainerCss(l), box: cueBoxCss(l), runs: cueRuns(c.text), ...(c.speaker ? { speaker: c.speaker } : {}) };
    }),
  };
}

/**
 * Page used to burn in subtitles: one transparent full-frame still per picture, bidi-aware.
 *
 * 1.9.0: `__prismCue(frame)` takes a SubtitleFrame (subtitleFrame, computed in Node with caption.ts cueLayout and
 * cueRuns, so the page holds no layout code and the preview and the burn-in cannot drift apart): every cue shown at
 * once in its own full-frame container, its box styled from the JSON, its runs as text nodes in spans (italic, bold,
 * underline), never HTML. Before 1.9.0 it took (text, speaker, w, h, bottom).
 */
export function buildSubtitleHost(fontCss: string): string {
  return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${CSP}">
<style>${fontCss}
html,body{margin:0;background:transparent;overflow:hidden;position:relative}
</style></head><body>
<script>
window.__prismCue = async (frame) => {
  const body = document.body;
  body.style.width = frame.width + 'px'; body.style.height = frame.height + 'px';
  while (body.lastChild && body.lastChild.nodeName !== 'SCRIPT') body.removeChild(body.lastChild);
  for (const c of frame.cues) {
    const wrap = document.createElement('div'); Object.assign(wrap.style, c.container); wrap.className = 'wrap';
    const box = document.createElement('div'); Object.assign(box.style, c.box); box.className = 'cue'; box.setAttribute('dir', 'auto');
    if (c.speaker) { const s = document.createElement('span'); s.className = 'speaker'; s.style.color = frame.speakerColor; s.textContent = c.speaker + ': '; box.appendChild(s); }
    for (const r of c.runs) {
      const span = document.createElement('span');
      if (r.i) span.style.fontStyle = 'italic';
      if (r.b) span.style.fontWeight = String(frame.boldWeight);
      if (r.u) span.style.textDecoration = 'underline';
      span.textContent = r.text;
      box.appendChild(span);
    }
    wrap.appendChild(box);
    body.appendChild(wrap);
  }
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  return true;
};
</script></body></html>`;
}
